import { Directive, EmbeddedViewRef, Input, OnDestroy, OnInit, TemplateRef, ViewContainerRef, ɵstringify as stringify, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FeatureFlag } from '@cco/feature-flags-model';
import { selectEnabledFeatureFlags } from '@core/auth2/store/auth2.reducer';
import { notNullish } from '@app/shared/rxjs/operators/standard-operators';

@Directive({
  selector: '[appIfFeatureEnabled]',
  standalone: true,
})
export class IfFeatureEnabledDirective<T = unknown> implements OnInit, OnDestroy {
  private store = inject(Store);
  private _viewContainer = inject(ViewContainerRef);

  private _context = new AppIfFeatureEnabledContext<T>();
  private readonly _thenTemplateRef: TemplateRef<AppIfFeatureEnabledContext<T>> | null = null;
  private _elseTemplateRef: TemplateRef<AppIfFeatureEnabledContext<T>> | null = null;
  private _thenViewRef: EmbeddedViewRef<AppIfFeatureEnabledContext<T>> | null = null;
  private _elseViewRef: EmbeddedViewRef<AppIfFeatureEnabledContext<T>> | null = null;


  enabledFeatureFlags: string[] = [];
  requiredFeatureFlags: FeatureFlag[];

  destroyed$ = new Subject<void>();

  constructor() {
    const templateRef = inject<TemplateRef<AppIfFeatureEnabledContext<T>>>(TemplateRef);

    this._thenTemplateRef = templateRef;
  }

  /**
   * All of these feature flags must be enabled for the element to render
   *
   * @param featureFlags
   */
  @Input()
  set appIfFeatureEnabled(featureFlags: FeatureFlag[] | FeatureFlag) {
    this.requiredFeatureFlags = typeof featureFlags === 'string' ? [ featureFlags ] : featureFlags;
    this._updateView();
  }

  @Input()
  set appIfFeatureEnabledElse(templateRef: TemplateRef<AppIfFeatureEnabledContext<T>>|null) {
    assertTemplate('appIfFeatureEnabledElse', templateRef);
    this._elseTemplateRef = templateRef;
    this._elseViewRef = null;  // clear previous view if any.
    this._updateView();
  }

  ngOnInit() {
    this.store
      .select(selectEnabledFeatureFlags).pipe(
        notNullish(),
        takeUntil(this.destroyed$),
      )
      .subscribe((enabledFeatureFlags) => {
        this.enabledFeatureFlags = enabledFeatureFlags;
        this._updateView();
      });
  }

  ngOnDestroy() {
    this.destroyed$.next();
  }

  private _updateView() {
    if (this.checkFeatureFlags()) {
      if (!this._thenViewRef) {
        this._viewContainer.clear();
        this._elseViewRef = null;
        if (this._thenTemplateRef) {
          this._thenViewRef =
            this._viewContainer.createEmbeddedView(this._thenTemplateRef, this._context);
        }

      }
    } else {
      if (!this._elseViewRef) {
        this._viewContainer.clear();
        this._thenViewRef = null;
        if (this._elseTemplateRef) {
          this._elseViewRef =
            this._viewContainer.createEmbeddedView(this._elseTemplateRef, this._context);
        }
      }
    }
  }

  private checkFeatureFlags() {
    let missing = false;

    if (this.requiredFeatureFlags) {
      if (!this.enabledFeatureFlags) {
        return false;
      }

      for (const featureFlag of this.requiredFeatureFlags) {
        if (!this.enabledFeatureFlags.includes(featureFlag)) {
          missing = true;
          break;
        }
      }
    }

    return !missing;
  }
}

export class AppIfFeatureEnabledContext<T = unknown> {
  public appIfFeatureEnabled: T = null;
}

function assertTemplate(property: string, templateRef: TemplateRef<any>|null): void {
  const isTemplateRefOrNull = !!(!templateRef || templateRef.createEmbeddedView);
  if (!isTemplateRefOrNull) {
    throw new Error(`${property} must be a TemplateRef, but received '${stringify(templateRef)}'.`);
  }
}
